import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import CopyRightInfo from "../../../core/common/copyRightInfo";
import { formatAmountForDisplay } from "../../../common/RazorpayConfig";
import useFetch from "../../../hooks/useFetch";
import { getAPIHeaders } from "../../../common/Utils";
import { API } from "../../../common/Constants";
import { Order, PaymentDetails, Subscription } from "../../../core/data/interface";
import CustomToast from "../../../core/common/customToast";
import { DEFAULT_THUMBNAIL_URL } from "../../../environment";
// Define Payment Verification Response interface
interface VerificationResponse {
  success: boolean;
  transactionId: string;
  status: string;
  message: string;
  data: {
    gatewayOrderId: string;
    currency: string;
    gateway: string;
    gatewayPaymentId: string;
    paymentMethod: string;
    amount: number;
  };
  timestamp: string;
}

interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

const OrderStatus = () => {
  const location = useLocation();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [orderDetails, setOrderDetails] = useState<Order | null>(null);
  const [razorpayResponse, setRazorpayResponse] = useState<RazorpayResponse | null>(null);
  const [verificationResponse, setVerificationResponse] = useState<VerificationResponse | null>(null);
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
    show: false,
    message: "Something went wrong please try again later",
    heading: "Oops! Something went wrong",
    type: "ERROR",
  });
  const [enrollmentFailed, setEnrollmentFailed] = useState(false);
  // API call setup
  const { isLoading, error, callFetch } = useFetch();


  useEffect(() => {
    if (error) {
      setToastSettings({
        show: true,
        message: error?.response?.data || 'Something went wrong please try again later',
        heading: "Error",
        type: "ERROR",
      });
    }
  }, [error]);

    // Get payment details from location state
  useEffect(() => {
      if (location.state?.paymentDetails) {
        setPaymentDetails(location.state.paymentDetails);
      }
      if (location.state?.razorpayResponse) {
        setRazorpayResponse(location.state.razorpayResponse);
      }
      if (location.state?.subscription) {
        setSubscription(location.state.subscription);
      }
      if (location.state?.orderDetails) {
        setOrderDetails(location.state.orderDetails);
      }
  }, [location.state]);


  useEffect(() => {
    if (razorpayResponse && !verificationResponse) {
      verifyPayment();
    } else if (razorpayResponse && verificationResponse) {
      enrollUserInCourse();
    }
  }, [razorpayResponse, verificationResponse]);

  useEffect(() => {
    if (orderDetails && orderDetails.orderStatus === 'PAID') {
      enrollUserInCourse();
    }
  }, [orderDetails]);




  const handleReCheck = () => {
    verifyPayment();
  };

  const formatPrice = (amount: number, currencyCode: string) => {
    return formatAmountForDisplay(amount, currencyCode);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const verifyPayment = () => {
    if (!razorpayResponse) {
      return;
    }

      const verificationPayload = {
        razorpay_order_id: razorpayResponse.razorpay_order_id,
        razorpay_payment_id: razorpayResponse.razorpay_payment_id,
        razorpay_signature: razorpayResponse.razorpay_signature
      };

      callFetch(
        API.VERIFY_ORDER,
        'POST',
        getAPIHeaders(),
        null,
        verificationPayload
      ).then((response) => {
        setVerificationResponse(response);
      }).catch((error) => {
      });
  }

  const enrollUserInCourse = () => {
    let endpoint = API.ENROLLED_COURSE.replace(':subscriptionPlanId', 
      String(subscription?.subscriptionPlanId || ''));
    if (verificationResponse?.transactionId) {
      endpoint += `?paymentTransactionId=${verificationResponse?.transactionId}`;
    }
    callFetch(
      endpoint,
      'POST',
      getAPIHeaders(),
      null,
      null
    ).then((response) => {
      setEnrollmentFailed(false);
    }).catch((error) => {
      setEnrollmentFailed(true);
    });
  }

  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted">
                Verifying your order...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!paymentDetails) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <i className="ti ti-alert-triangle display-1 text-danger mb-3"></i>
              <h4>Order Information Not Found</h4>
              <p className="text-muted">Unable to retrieve order details. Please contact support if you have been charged.</p>
              <Link to={all_routes.courses} className="btn btn-primary">
                <i className="ti ti-arrow-left me-2"></i>
                Back to Courses
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (verificationResponse && verificationResponse.status !== "SUCCESS") {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <i className="ti ti-alert-triangle display-1 text-warning mb-3"></i>
              <h4>Order Verification Failed</h4>
              <p className="text-muted mb-4">
                {(verificationResponse?.message || 'Order verification failed. Please try again.')}
              </p>
              
              {verificationResponse && (
                <div className="alert alert-warning text-start mb-4">
                  <h6>Order Details:</h6>
                  <div className="row">
                    <div className="col-md-6">
                      <small className="text-muted">Order ID:</small>
                      <div className="fw-medium">{orderDetails?.gatewayOrderId || orderDetails?.internalOrderId || 'N/A'}</div>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Status:</small>
                      <div className="fw-medium text-warning">{verificationResponse?.status}</div>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Fees:</small>
                      <div className="fw-medium">{formatPrice(verificationResponse?.data?.amount, verificationResponse?.data?.currency)}</div>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Gateway Payment ID:</small>
                      <div className="fw-medium">{verificationResponse?.data?.gatewayPaymentId}</div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="d-flex gap-2 justify-content-center">
                <button className="btn btn-warning" onClick={handleReCheck}>
                  <i className="ti ti-refresh me-2"></i>
                  Re-Check Order
                </button>
                <Link to={all_routes.courses} className="btn btn-outline-secondary">
                  <i className="ti ti-arrow-left me-2"></i>
                  Back to Courses
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // if (enrollmentFailed) {
  //   return (
  //     <div className="page-wrapper">
  //       <div className="content">
  //         <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
  //           <div className="text-center">
  //             <i className="ti ti-alert-triangle display-1 text-danger mb-3"></i>
  //             <h4>Enrollment Failed</h4>
  //             <p className="text-muted">Unable to enroll in course. Please contact support if you have been charged.</p>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <>
      <CustomToast
        show={toastSettings.show}
        message={toastSettings.message}
        heading={toastSettings.heading}
        type={toastSettings.type}
        onClose={() => {}}
        autoFadeTime={2000}
      />
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Order Status</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.index}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={all_routes.courses}>Courses</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Order Status
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}

          <div className="row justify-content-center">
            <div className="col-lg-8">
              {/* Success Message */}
              <div className="card border-success mb-4">
                <div className="card-body text-center py-5">
                  <div className="success-icon mb-4">
                    <i className="ti ti-check-circle text-success" style={{ fontSize: '5rem' }}></i>
                  </div>
                  <h2 className="text-success mb-3">Order Successful!</h2>
                  <p className="lead mb-4">
                    Congratulations! Your payment has been processed successfully and you now have access to the course.
                  </p>
                  
                  <div className="alert alert-success d-inline-block">
                    <i className="ti ti-shield-check me-2"></i>
                    Payment ID: <strong>{verificationResponse?.data.gatewayPaymentId || razorpayResponse?.razorpay_payment_id}</strong>
                  </div>
                </div>
              </div>

              {enrollmentFailed && ( 
                <div className="card border-danger mb-4">
                  <div className="card-body text-center py-5">
                      <div className="d-flex justify-content-center align-items-center">
                        <div className="text-center">
                          <i className="ti ti-alert-triangle display-1 text-danger mb-3"></i>
                          <h4>Enrollment Failed</h4>
                          <p className="text-muted">Unable to enroll in course. Please contact support if you have been charged.</p>
                        </div>
                      </div>
                    </div>
                  </div>      
              )}
              {/* Course Access Information */}
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="ti ti-book me-2"></i>
                    Batch Details
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4">
                      <ImageWithBasePath
                        src={subscription?.courses[0]?.courseMetadata?.thumbnail_url || DEFAULT_THUMBNAIL_URL}
                        alt={subscription?.planName || 'Batch'}
                        className="img-fluid rounded"
                        style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                      />
                    </div>
                    <div className="col-md-8">
                      <h4 className="mb-2 mt-2">{subscription?.planName}</h4>
                      <p className="text-muted mb-3">{subscription?.planDescription }</p>
                      
                      <div className="subscription-details p-3 bg-light rounded mb-3">
                        <h6 className="mb-2">
                          <i className="ti ti-crown me-2 text-warning"></i>
                          {subscription?.planName}
                        </h6>
                        <div className="row">
                          <div className="col-sm-6">
                            <small className="text-muted">Access Duration:</small>
                            <div className="fw-medium">
                              {subscription?.duration === 0 
                                ? 'Lifetime Access' 
                                : `${subscription?.duration} Month(s)`}
                            </div>
                          </div>
                          <div className="col-sm-6">
                            <small className="text-muted">Activated On:</small>
                            <div className="fw-medium">{formatDate(Date.now())}</div>
                          </div>
                        </div>
                      </div>

                      <div className="d-flex gap-2">
                        <Link to={all_routes.myAccount} className="btn btn-outline-primary">
                          <i className="ti ti-user me-2"></i>
                          View My Account
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Receipt */}
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="ti ti-receipt me-2"></i>
                    Payment Receipt
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="receipt-details">
                        {verificationResponse?.data.gatewayPaymentId && <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Payment ID:</span>
                          <span className="fw-medium">{verificationResponse?.data.gatewayPaymentId || 'N/A'}</span>
                        </div>}
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Order ID:</span>
                          <span className="fw-medium">{orderDetails?.gatewayOrderId || orderDetails?.internalOrderId || 'N/A'}</span>
                        </div>
                        {verificationResponse?.transactionId && <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Transaction ID:</span>
                          <span className="fw-medium">{verificationResponse?.transactionId || 'N/A'}</span>
                        </div>}
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Payment Method:</span>
                          <span className="fw-medium">{verificationResponse?.data.paymentMethod || 'Online Payment'}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Payment Date:</span>
                          <span className="fw-medium">{formatDate(Date.now())}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="amount-details">
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Batch:</span>
                          <span className="fw-medium">{subscription?.planName}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Original Fees:</span>
                          <span className="text-decoration-line-through text-muted">
                            {formatPrice(paymentDetails.originalAmount, paymentDetails.currencyCode)}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Offered Fees:</span>
                          <span className="text-decoration-line-through text-muted">
                            {formatPrice(paymentDetails.subtotalAmount, paymentDetails.currencyCode)}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Discount:</span>
                          <span className="text-success">
                            -{formatPrice(paymentDetails.totalDiscount, paymentDetails.currencyCode)}
                          </span>
                        </div>
                        <hr />
                        <div className="d-flex justify-content-between">
                          <span className="fw-bold">Total Paid:</span>
                          <span className="fw-bold text-primary fs-16">
                            {formatPrice(verificationResponse?.data.amount || paymentDetails.netAmount, verificationResponse?.data.currency || paymentDetails.currencyCode)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="ti ti-arrow-right me-2"></i>
                    What's Next?
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4">
                      <div className="text-center p-3">
                        <i className="ti ti-book text-primary mb-3" style={{ fontSize: '2.5rem' }}></i>
                        <h6>Access Your Course</h6>
                        <p className="text-muted small">
                          You can now access all course materials, videos, and resources.
                        </p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-center p-3">
                        <i className="ti ti-mail text-info mb-3" style={{ fontSize: '2.5rem' }}></i>
                        <h6>Check Your Email</h6>
                        <p className="text-muted small">
                          A confirmation email with course access details has been sent to you.
                        </p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-center p-3">
                        <i className="ti ti-headphones text-success mb-3" style={{ fontSize: '2.5rem' }}></i>
                        <h6>Need Help?</h6>
                        <p className="text-muted small">
                          Contact our support team if you have any questions about your course.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <CopyRightInfo/>
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default OrderStatus;
